import { useRef, useEffect, useCallback, useMemo } from 'react';

/**
 * useActiveTimer — tracks active (focused) play time in milliseconds.
 *
 * Auto-pauses when the tab/window loses focus (visibilitychange, blur)
 * and resumes when it regains focus (visibilitychange, focus).
 *
 * Uses refs only (no useState) so pause/resume causes zero re-renders.
 *
 * Usage:
 *   const timer = useActiveTimer();
 *   timer.start();              // reset & begin counting
 *   const ms = timer.stop();    // stop & return accumulated active ms
 */
export const useActiveTimer = () => {
  const accumulatedRef = useRef(0);
  const lastResumeRef = useRef(null); // Date.now() when last resumed, or null if paused
  const runningRef = useRef(false);

  // -- Internal helpers (not exposed) --

  const pause = useCallback(() => {
    if (runningRef.current && lastResumeRef.current !== null) {
      accumulatedRef.current += Date.now() - lastResumeRef.current;
      lastResumeRef.current = null;
      runningRef.current = false;
    }
  }, []);

  const resume = useCallback(() => {
    if (!runningRef.current && accumulatedRef.current >= 0) {
      // Only resume if we were previously started (accumulated >= 0 means start() was called)
      // We check lastResumeRef to avoid double-resuming.
      if (lastResumeRef.current === null) {
        lastResumeRef.current = Date.now();
        runningRef.current = true;
      }
    }
  }, []);

  // -- Public API --

  const start = useCallback(() => {
    accumulatedRef.current = 0;
    lastResumeRef.current = Date.now();
    runningRef.current = true;
  }, []);

  const stop = useCallback(() => {
    if (runningRef.current && lastResumeRef.current !== null) {
      accumulatedRef.current += Date.now() - lastResumeRef.current;
    }
    lastResumeRef.current = null;
    runningRef.current = false;
    return accumulatedRef.current;
  }, []);

  const getElapsed = useCallback(() => {
    let total = accumulatedRef.current;
    if (runningRef.current && lastResumeRef.current !== null) {
      total += Date.now() - lastResumeRef.current;
    }
    return total;
  }, []);

  // -- Auto-pause/resume on visibility & focus changes --

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden) {
        pause();
      } else {
        resume();
      }
    };

    const onBlur = () => pause();
    const onFocus = () => resume();

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
    };
  }, [pause, resume]);

  return useMemo(
    () => ({ start, stop, getElapsed }),
    [start, stop, getElapsed]
  );
};
